/* eslint-disable no-unused-vars */
import React, { useEffect, useState, forwardRef, useImperativeHandle, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  fetchCurrentAddressByEmployeeId,
  fetchPermanentAddressByEmployeeId,
  createEmployeeAddress,
  updateEmployeeAddressById,
  clearSelectedAddress,
  copyCurrentToPermanentAddress
} from "../../store/slices/employeeAddressSlice.js";
import {
  checkEmployeeCompleteness,
} from "../../store/slices/emergencyContactSlice";
import { fetchEmployeeById } from "../../store/slices/employeeDetailSlice";
import { PH_POSTAL_CODES, DUPLICATE_CITIES } from "../../utils/postalCodes"; 
import "bootstrap-icons/font/bootstrap-icons.css";

const EMPTY_ADDRESS = {
  addressNum: "",
  addressLine1: "",
  addressLine2: "", 
  city: "",
  municipality: "",
  province: "",
  region: "",
  postalCode: "",
  country: "Philippines",
};

// Helper to handle encoding issues and normalization for 'ñ'
const sanitizeString = (str) => {
  if (!str) return "";
  return str
    .replace(/Ã±/g, "ñ")
    .replace(/Ã‘/g, "Ñ")
    .normalize("NFC")
    .trim();
};

const EmployeeAddress = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const { id: employeeId } = useParams();
  const { current, permanent } = useSelector((state) => state.employeeAddress || {});

  const [formData, setFormData] = useState({
    current: { ...EMPTY_ADDRESS },
    permanent: { ...EMPTY_ADDRESS },
  });

  const [editingId, setEditingId] = useState({ current: null, permanent: null });
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [statusMsg, setStatusMsg] = useState({ text: "", type: "" });

  const [countries, setCountries] = useState([]);
  const [geoData, setGeoData] = useState({
    regions: [],
    current: { provinces: [], cities: [], barangays: [] },
    permanent: { provinces: [], cities: [], barangays: [] }
  });
  
  const [geoLoading, setGeoLoading] = useState({
    countries: false,
    regions: false,
    current: { provinces: false, cities: false, barangays: false },
    permanent: { provinces: false, cities: false, barangays: false }
  });

  /* ===================== API FETCHING ===================== */
  
  const fetchProvinces = useCallback(async (regionName, type) => {
    const region = geoData.regions.find(r => sanitizeString(r.name) === sanitizeString(regionName));
    if (!region) return;

    setGeoLoading(prev => ({ ...prev, [type]: { ...prev[type], provinces: true } }));
    
    try {
      if (regionName === "National Capital Region (NCR)") {
        // NCR logic: Fetch cities directly
        const res = await axios.get(`https://psgc.cloud/api/regions/${region.code}/cities-municipalities`);
        setGeoData(prev => ({ 
          ...prev, 
          [type]: { 
            ...prev[type], 
            provinces: [], 
            cities: res.data.sort((a,b) => a.name.localeCompare(b.name)), 
            barangays: [] 
          } 
        }));
        setFormData(prev => ({
          ...prev,
          [type]: { ...prev[type], province: "National Capital Region (NCR)" }
        }));
      } else {
        const res = await axios.get(`https://psgc.cloud/api/regions/${region.code}/provinces`);
        setGeoData(prev => ({ ...prev, [type]: { ...prev[type], provinces: res.data, cities: [], barangays: [] } }));
      }
    } catch (err) { console.error(err); } 
    finally { setGeoLoading(prev => ({ ...prev, [type]: { ...prev[type], provinces: false } })); }
  }, [geoData.regions]);

  const fetchCities = useCallback(async (provinceName, type) => {
    const province = geoData[type].provinces.find(p => sanitizeString(p.name) === sanitizeString(provinceName));
    if (!province) return;
    setGeoLoading(prev => ({ ...prev, [type]: { ...prev[type], cities: true } }));
    try {
      const res = await axios.get(`https://psgc.cloud/api/provinces/${province.code}/cities-municipalities`);
      setGeoData(prev => ({ ...prev, [type]: { ...prev[type], cities: res.data, barangays: [] } }));
    } catch (err) { console.error(err); } 
    finally { setGeoLoading(prev => ({ ...prev, [type]: { ...prev[type], cities: false } })); }
  }, [geoData]);

  const fetchBarangays = useCallback(async (cityName, type) => {
    const city = geoData[type].cities.find(c => sanitizeString(c.name) === sanitizeString(cityName));
    if (!city) return;
    setGeoLoading(prev => ({ ...prev, [type]: { ...prev[type], barangays: true } }));
    try {
      const res = await axios.get(`https://psgc.cloud/api/cities-municipalities/${city.code}/barangays`);
      setGeoData(prev => ({ ...prev, [type]: { ...prev[type], barangays: res.data } }));
    } catch (err) { console.error(err); } 
    finally { setGeoLoading(prev => ({ ...prev, [type]: { ...prev[type], barangays: false } })); }
  }, [geoData]);

  useEffect(() => {
    if (!employeeId) return;
    dispatch(fetchCurrentAddressByEmployeeId(employeeId));
    dispatch(fetchPermanentAddressByEmployeeId(employeeId));
// 1. Set loading flags smoothly using a unified state update
setGeoLoading(prev => ({ ...prev, countries: true, regions: true }));

// 2. Fetch Countries with explicit error handling catch blocks
axios.get("https://restcountries.com/v3.1/all?fields=name")
  .then(res => {
    const sortedCountries = res.data.map(c => c.name.common).sort();
    setCountries(sortedCountries);
  })
  .catch(err => {
    console.error("Countries API failed to load:", err);
    // Fallback array so your UI layout doesn't break
    setCountries(["Philippines"]); 
  })
  .finally(() => setGeoLoading(prev => ({ ...prev, countries: false })));

// 3. Fetch Regions with a resilient fallback system
axios.get("https://psgc.cloud/api/regions")
  .then(res => {
    setGeoData(prev => ({ ...prev, regions: res.data }));
  })
  .catch(err => {
    console.error("PSGC Regions API unreachable:", err);
        
    setGeoData(prev => ({ ...prev, regions: [] })); 
  })
  .finally(() => setGeoLoading(prev => ({ ...prev, regions: false })));

return () => dispatch(clearSelectedAddress());  }, [employeeId, dispatch]);

  useEffect(() => {
    setFormData({
      current: current ? { ...EMPTY_ADDRESS, ...current } : { ...EMPTY_ADDRESS },
      permanent: permanent ? { ...EMPTY_ADDRESS, ...permanent } : { ...EMPTY_ADDRESS },
    });
    setEditingId({ current: current?.id || null, permanent: permanent?.id || null });
    if (!current?.id && !permanent?.id) setIsEditMode(false);
  }, [current, permanent]);

  useImperativeHandle(ref, () => ({ submit: async () => await handleGlobalSave() }));

  const handleChange = (e, type) => {
    const { name, value } = e.target;
    setStatusMsg({ text: "", type: "" });
    if (errors[`${type}_${name}`]) setErrors(prev => ({ ...prev, [`${type}_${name}`]: false }));
    
    setFormData((prev) => {
      let updatedSection = { 
        ...prev[type], 
        [name]: value,
        ...(name === "country" && value !== "Philippines" && { region: "", province: "", city: "", municipality: "", addressLine2: "", postalCode: "" }),
        ...(name === "region" && { province: "", city: "", municipality: "", addressLine2: "", postalCode: "" }),
        ...(name === "province" && { city: "", municipality: "", addressLine2: "", postalCode: "" }),
        ...(name === "city" && { addressLine2: "" }),
      };

      if (name === "city") {
        const cleanCity = sanitizeString(value);
        let lookupKey = cleanCity;

        const isDupe = DUPLICATE_CITIES.some(dupe => 
          sanitizeString(cleanCity).includes(sanitizeString(dupe))
        );

        if (isDupe) {
          lookupKey = `${cleanCity}|${sanitizeString(updatedSection.province)}`;
        }

        updatedSection.postalCode = PH_POSTAL_CODES[lookupKey] || "";
        updatedSection.municipality = cleanCity;
      }

      return { ...prev, [type]: updatedSection };
    });

    if (name === "region") fetchProvinces(value, type);
    if (name === "province") fetchCities(value, type);
    if (name === "city") fetchBarangays(value, type);
  };

  const handleGlobalSave = async () => {
    const idToUse = employeeId; 
    if (!idToUse || !isEditMode) return;
    setIsSaving(true);
    try {
      const saveTask = async (type, addressId, uuid) => {
        const { id, createdAt, updatedAt, employeeId: e1, ...cleanData } = formData[type];
        if (type === "permanent" && !addressId && !cleanData.addressLine1?.trim()) return null;
        const payload = { ...cleanData, employeeId: uuid, addressType: type.charAt(0).toUpperCase() + type.slice(1) };
        // dispatch(fetchEmployeeById(employeeId))
        return addressId 
          ? dispatch(updateEmployeeAddressById({ id: addressId, data: payload })).unwrap()
          : dispatch(createEmployeeAddress(payload)).unwrap();
          
      };
      await Promise.all([saveTask("current", editingId.current, idToUse), saveTask("permanent", editingId.permanent, idToUse),
      ]);
await dispatch(checkEmployeeCompleteness(employeeId)).unwrap();
      await dispatch(fetchEmployeeById(idToUse)).unwrap();
      setStatusMsg({ text: "Saved successfully!", type: "success" });
      setIsEditMode(false);
    } catch (err) { setStatusMsg({ text: err.message || "Save failed", type: "danger" }); }
    finally { setIsSaving(false); }
    
  };

  const handleCopyCurrentToPermanent = () => {
    setFormData(prev => ({
      ...prev,
      permanent: { ...prev.current, id: editingId.permanent, addressType: "Permanent" }
    }));
    dispatch(copyCurrentToPermanentAddress(employeeId))
    setGeoData(prev => ({ ...prev, permanent: { ...prev.current } }));
    setStatusMsg({ text: "Current address copied!", type: "success" });
    setTimeout(() => setStatusMsg({ text: "", type: "" }), 2000);
  };

  const handleCancel = () => {
    setFormData({
      current: current ? { ...EMPTY_ADDRESS, ...current } : { ...EMPTY_ADDRESS },
      permanent: permanent ? { ...EMPTY_ADDRESS, ...permanent } : { ...EMPTY_ADDRESS },
    });
    setIsEditMode(false);
    setErrors({});
  };

  const inputStyle = (type, name) => ({ 
    height: "32px", fontSize: "0.85rem", borderRadius: '8px',
    border: !isEditMode ? 'none' : (errors[`${type}_${name}`] ? '1.5px solid #dc3545' : '1px solid rgba(0,0,0,0.1)'),
    backgroundColor: !isEditMode ? '#f8fafc' : '#ffffff'
  });

  const renderSection = (type, title) => {
    const data = formData[type];
    const isPH = data.country === "Philippines";
    const isNCR = data.region === "National Capital Region (NCR)";
    const isMandatory = type === "current";

    return (
      <div className="card shadow-sm border-0 bg-white h-100">
        <div className="d-flex justify-content-between align-items-center mb-3 px-2">
          <h6 className="fw-bold text-muted extra-small text-uppercase m-0">{title}</h6>
          {type === 'permanent' && isEditMode && (
            <button type="button" className="btn btn-outline-primary py-0 px-2 extra-small fw-bold border-0" 
                    onClick={handleCopyCurrentToPermanent} style={{ fontSize: '10px' }}>
              <i className="bi bi-copy me-1"></i>Copy Current
            </button>
          )}
        </div>
        <div className="row g-2 px-2">
          <div className="col-md-12">
            <label className="fw-semibold extra-small mb-1 text-muted text-uppercase d-flex align-items-center">
              Country {isMandatory && <span className="text-danger ms-1">*</span>}
            </label>
            <select name="country" value={data.country || ""} onChange={(e) => handleChange(e, type)} disabled={!isEditMode} className="form-select form-select-sm shadow-none" style={inputStyle(type, 'country')}>
              <option value="">Select Country</option>
              {countries.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="col-md-6">
            <label className="fw-semibold extra-small mb-1 text-muted text-uppercase d-flex align-items-center">
              Region {isMandatory && <span className="text-danger ms-1">*</span>}
            </label>
            {isPH ? (
              <select name="region" value={data.region || ""} onChange={(e) => handleChange(e, type)} disabled={!isEditMode} className="form-select form-select-sm shadow-none" style={inputStyle(type, 'region')}>
                <option value="">Select Region</option>
                {geoData.regions.map(r => <option key={r.code} value={r.name}>{r.name}</option>)}
              </select>
            ) : <input type="text" name="region" value={data.region || ""} onChange={(e) => handleChange(e, type)} disabled={!isEditMode} className="form-control form-control-sm shadow-none" style={inputStyle(type, 'region')} />}
          </div>

          <div className="col-md-6">
            <label className="fw-semibold extra-small mb-1 text-muted text-uppercase d-flex align-items-center">
              Province {isMandatory && <span className="text-danger ms-1">*</span>}
            </label>
            {isNCR ? (
              <input type="text" readOnly value="National Capital Region (NCR)" className="form-control form-control-sm shadow-none bg-light" style={inputStyle(type, 'province')} />
            ) : isPH && geoData[type].provinces.length > 0 ? (
              <select name="province" value={data.province || ""} onChange={(e) => handleChange(e, type)} disabled={!isEditMode} className="form-select form-select-sm shadow-none" style={inputStyle(type, 'province')}>
                <option value="">Select Province</option>
                {geoData[type].provinces.map(p => <option key={p.code} value={p.name}>{p.name}</option>)}
              </select>
            ) : <input type="text" name="province" value={data.province || ""} onChange={(e) => handleChange(e, type)} disabled={!isEditMode} className="form-control form-control-sm shadow-none" style={inputStyle(type, 'province')} />}
          </div>

          <div className="col-md-6">
            <label className="fw-semibold extra-small mb-1 text-muted text-uppercase d-flex align-items-center">
              City / Mun. {isMandatory && <span className="text-danger ms-1">*</span>}
            </label>
            {isPH && geoData[type].cities.length > 0 ? (
              <select name="city" value={data.city || ""} onChange={(e) => handleChange(e, type)} disabled={!isEditMode} className="form-select form-select-sm shadow-none" style={inputStyle(type, 'city')}>
                <option value="">Select City</option>
                {geoData[type].cities.map(c => <option key={c.code} value={c.name}>{c.name}</option>)}
              </select>
            ) : <input type="text" name="city" value={data.city || ""} onChange={(e) => handleChange(e, type)} disabled={!isEditMode} className="form-control form-control-sm shadow-none" style={inputStyle(type, 'city')} />}
          </div>

          <div className="col-md-6">
            <label className="fw-semibold extra-small mb-1 text-muted text-uppercase d-flex align-items-center">
              Barangay {isMandatory && <span className="text-danger ms-1">*</span>}
            </label>
            {isPH && geoData[type].barangays.length > 0 ? (
              <select name="addressLine2" value={data.addressLine2 || ""} onChange={(e) => handleChange(e, type)} disabled={!isEditMode} className="form-select form-select-sm shadow-none" style={inputStyle(type, 'addressLine2')}>
                <option value="">Select Barangay</option>
                {geoData[type].barangays.map(b => <option key={b.code} value={b.name}>{b.name}</option>)}
              </select>
            ) : <input type="text" name="addressLine2" value={data.addressLine2 || ""} onChange={(e) => handleChange(e, type)} disabled={!isEditMode} className="form-control form-control-sm shadow-none" style={inputStyle(type, 'addressLine2')} />}
          </div>

          <div className="col-md-6"><label className="fw-semibold extra-small mb-1 text-muted text-uppercase">Municipality</label><input type="text" name="municipality" readOnly={isPH} disabled={!isEditMode} value={data.municipality || ""} onChange={(e) => handleChange(e, type)} className={`form-control form-control-sm shadow-none ${isPH ? 'bg-light' : ''}`} style={inputStyle(type, 'municipality')} /></div>
          <div className="col-md-6"><label className="fw-semibold extra-small mb-1 text-muted text-uppercase">House/Blk/Lot</label><input type="text" name="addressNum" disabled={!isEditMode} value={data.addressNum || ""} onChange={(e) => handleChange(e, type)} className="form-control form-control-sm shadow-none" style={inputStyle(type, 'addressNum')} /></div>
          <div className="col-md-8"><label className="fw-semibold extra-small mb-1 text-muted text-uppercase">Street/Village Name</label><input type="text" name="addressLine1" disabled={!isEditMode} value={data.addressLine1 || ""} onChange={(e) => handleChange(e, type)} className="form-control form-control-sm shadow-none" style={inputStyle(type, 'addressLine1')} /></div>
          <div className="col-md-4"><label className="fw-semibold extra-small mb-1 text-muted text-uppercase">Postal Code</label><input type="text" name="postalCode" disabled={!isEditMode} value={data.postalCode || ""} onChange={(e) => handleChange(e, type)} className="form-control form-control-sm shadow-none" style={inputStyle(type, 'postalCode')} /></div>
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid py-2 px-0">
      <style>{`
        .extra-small { font-size: 0.7rem; letter-spacing: 0.02em; }
        .progress-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(255,255,255,0.7); z-index: 100; display: flex; align-items: center; justify-content: center; border-radius: 8px; }
      `}</style>
      <div className="bg-white p-3 rounded-3 shadow-sm border mb-4 position-relative">
        {isSaving && <div className="progress-overlay"><div className="spinner-border text-primary"></div></div>}
        <div className="d-flex justify-content-between align-items-center px-3 py-2 rounded-2 mb-4" style={{ backgroundColor: "#1e293b", minHeight: '44px' }}>
          <h6 className="text-white m-0 extra-small text-uppercase fw-bold"> </h6>
          <div className="d-flex align-items-center gap-3">
             {statusMsg.text && (
              <span className={`fw-bold text-uppercase d-flex align-items-center text-${statusMsg.type}`} style={{ fontSize: '0.65rem' }}>
                <i className={`bi ${statusMsg.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'} me-2`}></i>
                {statusMsg.text}
              </span>
            )}
            {!isEditMode ? (
              <button className="btn btn-sm py-0 px-2" style={{ backgroundColor: "#0f172a", width: '32px', height: '28px' }} onClick={() => setIsEditMode(true)}>
                <i className="bi bi-pencil-square text-white"></i>
              </button>
            ) : (
              <div className="d-flex gap-2">
                <button className="btn btn-success btn-sm py-0 px-2" style={{ width: '32px', height: '28px' }} onClick={handleGlobalSave} disabled={isSaving}>
                  <i className="bi bi-check-lg text-white"></i>
                </button>
                <button className="btn btn-sm py-0 px-2" style={{ backgroundColor: "#64748b", width: '32px', height: '28px' }} onClick={handleCancel}>
                  <i className="bi bi-x-lg text-white"></i>
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="row g-4 px-2">
          <div className="col-lg-6 border-end">{renderSection("current", "Current Address")}</div>
          <div className="col-lg-6">{renderSection("permanent", "Permanent Address")}</div>
        </div>
      </div>
    </div>
  );
});

export default EmployeeAddress;